/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, useEffect } from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Grid, breakpoints } from '@coursera/cds-core';

import OtherTopics from 'bundles/browse/components/Domain/OtherTopics';
import withNotFound from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import CourseCards, { coursesFragment } from 'bundles/partners-graphql/components/CourseCards';
import DegreeCards, { degreesUpsellMastertracksFragment } from 'bundles/partners-graphql/components/DegreeCards';
import Instructors, { instructorsFragment } from 'bundles/partners-graphql/components/Instructors';
import PartnerHeader from 'bundles/partners-graphql/components/PartnerHeader';
import PartnerLinks, { partnerLinksFragment } from 'bundles/partners-graphql/components/PartnerLinks';
import SpecializationCards, {
  professionalCertificatesFragment,
  specializationFragment,
} from 'bundles/partners-graphql/components/SpecializationCards';
import type {
  PartnerPageQuery_PartnersV1Resource_shortName_partners as Partner,
  PartnerPageQuery,
  PartnerPageQueryVariables,
} from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import getAllDegrees from 'bundles/partners-graphql/queries/GetAllDegrees.graphql';
import type {
  PartnersPremiumProductCollections_LearningProductFragment as PartnersPremiumProductCollectionsLearningProductFragment,
  PartnersPremiumProductCollectionsQuery,
} from 'bundles/partners-graphql/queries/__generated__/GetAllDegrees';
import { CourseraMetatags } from 'bundles/seo';
import PartnerPageGoogleSchemaMarkup from 'bundles/seo/components/PartnerPageGoogleSchemaMarkup';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';

import _t from 'i18n!nls/partners-graphql';

const partnerPageStyles = {
  partnerPage: css`
    overflow-x: hidden;

    hr {
      width: 100%;
    }

    .bt3-container {
      &.body-container {
        max-width: 975px;
        min-height: 60vh;
      }
    }

    .internal-linking-wrapper-cds {
      margin: 60px auto;
      margin-bottom: 30px;
      padding: 0 16px;

      .internal-linking-container-cds {
        max-width: 1114px;
        margin-left: auto;
        margin-right: auto;

        ${breakpoints.down('md')} {
          margin: 45px auto;
        }

        a {
          &:hover {
            text-decoration: none;
            color: #000;
          }
        }
      }
    }
  `,
};

type PropsFromCaller = {
  params: {
    shortName: string;
  };
};

type PropsFromGraphql = {
  partners: Array<Partner | null>;
  isLoading?: boolean;
};

export type Props = PropsFromGraphql & {
  degrees: PartnersPremiumProductCollectionsLearningProductFragment[];
};

export const PartnerPage = ({ partners, isLoading, degrees }: Props) => {
  const partner = partners?.length ? partners[0] : null;
  const location = useLocation();

  useEffect(() => {
    // This page can be accessed through CSR routing so we programmatically scroll to the top of the page
    window.scrollTo(0, 0);

    if (partner && partner.homeLink && typeof window !== 'undefined') {
      // Some partners don't want us to host their page, so we must redirect to their page.
      // *cough* stanford...
      window.location.href = partner.homeLink;
    }
  }, [partner]);

  if (isLoading) {
    return (
      <div style={{ minHeight: '60vh' }}>
        <LoadingIcon />
      </div>
    );
  } else if (!partner) {
    return null;
  }

  if (partner.homeLink) {
    return (
      <p className="redirect-message">
        Redirecting to <a href={partner.homeLink}>{partner.name}</a>…
      </p>
    );
  }

  const { isInternationalSubdomain } = getSubdomainDetails(location);
  const metaTitle = `${partner.name} Online Courses`;
  const degreesData = degrees.filter((degree) => degree.partners[0]?.id === partner.id);
  return (
    <div css={partnerPageStyles.partnerPage} className="rc-PartnerPage styleguide">
      <PartnerHeader partner={partner} />
      <div className="bt3-container body-container">
        <hr />
        <DegreeCards degrees={degreesData} mastertracks={partner.mastertracks} />
        {(partner.specializations?.elements?.length ?? 0) > 0 && (
          <Fragment>
            <SpecializationCards
              specializations={partner.specializations}
              professionalCertificates={partner.professionalCertificates}
            />
          </Fragment>
        )}
        {partner.courseIds?.length > 0 && (
          <Fragment>
            <CourseCards title={_t('Courses')} partnerShortName={partner.shortName} courses={partner.courses} />
            <hr />
          </Fragment>
        )}
        {partner.instructorIds?.length > 0 && <Instructors instructors={partner.instructors} />}
        <hr />
        <PartnerLinks partner={partner} />
      </div>
      <Grid container className="internal-linking-wrapper-cds">
        <Grid item xs={12} className="internal-linking-container-cds">
          <OtherTopics />
        </Grid>
      </Grid>
      <div>
        {/* For robots */}
        <PartnerPageGoogleSchemaMarkup partner={partner} />
        <CourseraMetatags
          metaNameAndProperties={{
            title: metaTitle,
            description: partner.description ?? undefined,
            absoluteImageHref: partner.squareLogo ?? undefined,
            useFollowForDisablingIndexing: true,
          }}
          hrefLangOverrides={(links) => (isInternationalSubdomain ? [] : links)}
        />
      </div>
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  graphql<PropsFromCaller, PartnerPageQuery, PartnerPageQueryVariables, PropsFromGraphql>(
    gql`
      query PartnerPageQuery($shortName: String!, $liveOnly: Boolean!) {
        PartnersV1Resource {
          shortName(shortName: $shortName, limit: 1) {
            partners: elements {
              id
              shortName
              homeLink
              courseIds
              courses(includeHiddenS12ns: true, showHidden: true, withCorrectBehavior: true, limit: 1000) {
                ...CoursesFragment
              }
              specializations {
                ...SpecializationsFragment
              }
              instructorIds
              instructors(limit: 1000) {
                ...InstructorsFragment
              }
              ...PartnerHeaderFragment
              ...PartnerLinksFragment
              mastertracks(liveOnly: $liveOnly) {
                ...MastertracksFragment
              }
              professionalCertificates {
                ...ProfessionalCertificates
              }
            }
          }
        }
      }
      ${PartnerHeader.fragments.partnerHeaderFragment}
      ${coursesFragment}
      ${specializationFragment}
      ${partnerLinksFragment}
      ${instructorsFragment}
      ${degreesUpsellMastertracksFragment}
      ${professionalCertificatesFragment}
    `,
    {
      props: ({ data }) => {
        const partners = data?.PartnersV1Resource?.shortName?.partners || [];
        return {
          isLoading: data?.loading,
          partners,
        };
      },
      options({ params: { shortName } }) {
        return {
          variables: {
            shortName,
            liveOnly: !user.isSuperuser(),
          },
        };
      },
    }
  ),
  graphql(getAllDegrees, {
    options: () => ({
      context: {
        clientName: 'gatewayGql',
      },
    }),
    props: ({ data }: { data?: PartnersPremiumProductCollectionsQuery }) => {
      return {
        degrees: data?.PremiumProductCollections?.queryCollection || [],
      };
    },
  }),
  withNotFound({
    test: ({ partners, isLoading }) => (!isLoading && !partners) || (!isLoading && partners.length === 0),
  })
)(PartnerPage);
